import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { PageHero } from "@/components/Section";
import { BureauContent } from "@/pages/Bureau";
import { SEO } from "@/components/SEO";
import api, { formatApiError } from "@/lib/api";
import { REGIONS } from "@/lib/constants";
import { MapPin, User, Mail, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";

const markerIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const emptyReq = { name: "", region: "", city: "", full_name: "", email: "", phone: "", motivation: "" };

export default function Comites() {
  const [committees, setCommittees] = useState([]);
  const [region, setRegion] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [req, setReq] = useState(emptyReq);
  const [submitting, setSubmitting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("vue") === "bureau" ? "bureau" : "comites";
  const setTab = (key) => setSearchParams(key === "bureau" ? { vue: "bureau" } : {});

  useEffect(() => {
    api.get("/committees").then(({ data }) => setCommittees(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (region ? committees.filter((c) => c.region === region) : committees),
    [committees, region]
  );

  const regionsAvailable = useMemo(
    () => [...new Set(committees.map((c) => c.region))].sort(),
    [committees]
  );

  const submitRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/committee-request", req);
      toast.success(data.message);
      setReq(emptyReq);
      setShowForm(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="comites-page">
      <SEO title="Comités & bureau" description="Le mouvement DEFAR - Les Bâtisseurs est présent dans toutes les régions du Sénégal et dans la diaspora, animé par son Comité de Pilotage (COPIL)." />
      <PageHero
        overline="Organisation"
        title="Comités & bureau"
        subtitle="Le mouvement est présent dans toutes les régions du Sénégal et au sein de la diaspora, animé par son Comité de Pilotage (COPIL)."
      />

      <div className="border-b border-border bg-white sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-2">
          {[["comites", "Les comités"], ["bureau", "Le bureau"]].map(([key, label]) => (
            <button
              key={key}
              data-testid={`comites-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-5 py-4 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === key ? "border-brand-green text-brand-green" : "border-transparent text-slate-500 hover:text-brand-blue"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === "bureau" && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <BureauContent />
        </section>
      )}

      {tab === "comites" && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-slate-700">Filtrer par région :</span>
            <select
              data-testid="region-filter"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="border border-border bg-white px-4 py-2 rounded-sm text-sm focus:outline-none focus:border-brand-blue"
            >
              <option value="">Toutes les régions</option>
              {regionsAvailable.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <button
            data-testid="open-committee-request"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 bg-brand-green text-white hover:bg-brand-greenhover transition-colors px-5 py-2.5 text-sm font-semibold"
          >
            <Plus size={16} /> Créer un comité
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 h-[500px] border border-border rounded-sm overflow-hidden">
            <MapContainer center={[14.5, -14.5]} zoom={6} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {filtered.map((c) => (
                <Marker key={c.id} position={[c.lat, c.lng]} icon={markerIcon}>
                  <Popup>
                    <div className="text-sm">
                      <strong>{c.name}</strong>
                      <br />
                      {c.city}, {c.region}
                      <br />
                      {c.responsable && <>Responsable : {c.responsable}<br /></>}
                      {c.contact}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          <div className="lg:col-span-5">
            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="animate-spin text-brand-blue" size={28} /></div>
            ) : (
              <div className="space-y-4 max-h-[500px] overflow-y-auto pr-1">
                {filtered.map((c, i) => (
                  <div key={c.id} data-testid={`committee-${i}`} className="bg-white border border-border p-5 rounded-sm hover:border-brand-blue transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-bold text-slate-900">{c.name}</h3>
                      <span className="text-xs bg-brand-blue/5 text-brand-blue font-semibold px-2 py-1 shrink-0">{c.region}</span>
                    </div>
                    <p className="mt-2 text-sm text-slate-600">{c.description}</p>
                    <div className="mt-3 space-y-1.5 text-sm text-slate-500">
                      <p className="flex items-center gap-2"><MapPin size={14} /> {c.city}, {c.region}</p>
                      {c.responsable && <p className="flex items-center gap-2"><User size={14} /> {c.responsable}</p>}
                      {c.contact && <p className="flex items-center gap-2"><Mail size={14} /> {c.contact}</p>}
                    </div>
                  </div>
                ))}
                {filtered.length === 0 && <p className="text-slate-500 text-sm py-10 text-center">Aucun comité dans cette région.</p>}
              </div>
            )}
          </div>
        </div>
      </section>
      )}

      {showForm && (
        <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-sm max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-xl font-bold text-slate-900">Demande de création de comité</h3>
              <button onClick={() => setShowForm(false)}><X size={22} className="text-slate-400" /></button>
            </div>
            <form onSubmit={submitRequest} className="p-6 space-y-4">
              <Field label="Nom du comité souhaité" value={req.name} onChange={(v) => setReq({ ...req, name: v })} required testid="req-name" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Région *</label>
                  <select required value={req.region} onChange={(e) => setReq({ ...req, region: e.target.value })} data-testid="req-region" className="w-full border border-border px-4 py-2 rounded-sm text-sm focus:outline-none focus:border-brand-blue">
                    <option value="">Choisir</option>
                    {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <Field label="Ville" value={req.city} onChange={(v) => setReq({ ...req, city: v })} testid="req-city" />
              </div>
              <Field label="Votre nom complet" value={req.full_name} onChange={(v) => setReq({ ...req, full_name: v })} required testid="req-fullname" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Email" type="email" value={req.email} onChange={(v) => setReq({ ...req, email: v })} required testid="req-email" />
                <Field label="Téléphone" value={req.phone} onChange={(v) => setReq({ ...req, phone: v })} testid="req-phone" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Motivation</label>
                <textarea value={req.motivation} onChange={(e) => setReq({ ...req, motivation: e.target.value })} rows={3} data-testid="req-motivation" className="w-full border border-border px-4 py-2 rounded-sm text-sm focus:outline-none focus:border-brand-blue" />
              </div>
              <button type="submit" disabled={submitting} data-testid="req-submit" className="w-full bg-brand-green text-white hover:bg-brand-greenhover transition-colors py-3 font-semibold disabled:opacity-60">
                {submitting ? "Envoi..." : "Soumettre la demande"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", required, testid }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}{required && " *"}</label>
    <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full border border-border px-4 py-2 rounded-sm text-sm focus:outline-none focus:border-brand-blue" />
  </div>
);
